import { useEffect, useState } from 'react';
import {
  partyDirectoryApi,
  type CommercialSummary,
  type QuoteDecision,
} from './party-directory-api';

/** Qualification manuelle des destinataires de devis ; aucune fiche Prospect n'est créée. */
export function CommercialStatistics({
  csrfToken,
}: {
  readonly csrfToken: string;
}): React.JSX.Element {
  const [page, setPage] = useState(0);
  const [groupQuery, setGroupQuery] = useState('');
  const [view, setView] = useState<CommercialSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<
    Record<string, { outcome: QuoteDecision | ''; group: string }>
  >({});

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(
      () => {
        void partyDirectoryApi.commercialSummary(page, groupQuery).then(
          (data) => {
            if (active) {
              setView(data);
              setError(null);
            }
          },
          () => {
            if (active) setError('Statistiques commerciales indisponibles.');
          },
        );
      },
      groupQuery ? 200 : 0,
    );
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [page, groupQuery]);

  const classify = async (quote: CommercialSummary['quotes'][number]): Promise<void> => {
    const choice = choices[quote.id];
    if (!choice?.outcome) return;
    if (
      !window.confirm(
        'Confirmer ce destinataire et sa situation commerciale pour le devis sélectionné ?',
      )
    )
      return;
    setBusy(true);
    try {
      await partyDirectoryApi.classifyQuote(
        quote.id,
        choice.outcome,
        choice.group || null,
        csrfToken,
      );
      setView(await partyDirectoryApi.commercialSummary(page, groupQuery));
      setChoices((current) => {
        const next = { ...current };
        delete next[quote.id];
        return next;
      });
      setError(null);
    } catch {
      setError('Qualification non enregistrée. Vérifiez le devis et le groupe choisi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="client-directory__statistics"
      aria-label="Statistiques prospects et clients"
    >
      <h2>Prospects / Clients</h2>
      <p>
        Un Prospect est un destinataire de devis envoyé sans acceptation. La qualification est
        statistique uniquement : elle ne crée ni fiche, ni compte, ni accès.
      </p>
      {error && (
        <p role="alert" className="client-directory__error tosho-feedback is-error">
          {error}
        </p>
      )}
      {view && (
        <>
          <p>
            <strong>{view.clientCount}</strong> Clients Tosho confirmés ·{' '}
            <strong>{view.quoteCount}</strong> devis Freebe
          </p>
          <p>
            {view.ratio === null
              ? `Ratio non calculable : ${view.unclassifiedCount} devis à qualifier, ${view.staleCount} décision(s) à revalider après import.`
              : `${view.prospectCount} Prospects / ${view.clientCount} Clients = ${(view.ratio * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
          </p>
          <p>
            Le libellé Client du XLSX n'est pas une preuve de relation. Regroupez seulement les
            devis dont vous avez confirmé le même destinataire.
          </p>
          <label className="client-directory__search">
            Rechercher un destinataire déjà confirmé (nom ou identifiant)
            <input
              type="search"
              value={groupQuery}
              maxLength={120}
              onChange={(event) => setGroupQuery(event.currentTarget.value)}
            />
          </label>
          <p>
            Les groupes proposés sont des choix manuels ; cette recherche ne crée aucun
            rapprochement.
          </p>
          <ul className="client-directory__list">
            {view.quotes.map((quote) => {
              const choice = choices[quote.id] ?? {
                outcome: '',
                group: quote.recipientGroupId ?? '',
              };
              return (
                <li key={quote.id}>
                  <strong>
                    {quote.reference} · {quote.clientLabel ?? 'Destinataire non renseigné'}
                  </strong>
                  <span>
                    Statut : {quote.status} · État : {quote.state ?? 'non renseigné'}
                  </span>
                  <small>
                    {quote.needsReview ? 'Décision humaine requise' : `Qualifié : ${quote.outcome}`}
                  </small>
                  <label>
                    Situation vérifiée
                    <select
                      value={choice.outcome}
                      onChange={(event) =>
                        setChoices((current) => ({
                          ...current,
                          [quote.id]: {
                            ...choice,
                            outcome: event.currentTarget.value as QuoteDecision | '',
                          },
                        }))
                      }
                    >
                      <option value="">Choisir</option>
                      <option value="SENT_UNACCEPTED">Devis envoyé, non accepté</option>
                      <option value="ACCEPTED">Devis accepté / Client</option>
                      <option value="EXCLUDED">Non envoyé ou hors statistiques</option>
                    </select>
                  </label>
                  <label>
                    Destinataire confirmé
                    <select
                      value={choice.group}
                      onChange={(event) =>
                        setChoices((current) => ({
                          ...current,
                          [quote.id]: { ...choice, group: event.currentTarget.value },
                        }))
                      }
                    >
                      <option value="">Nouveau destinataire statistique</option>
                      {choice.group && !view.groups.some((group) => group.id === choice.group) && (
                        <option value={choice.group}>
                          Groupe déjà choisi · {choice.group.slice(0, 8)}
                        </option>
                      )}
                      {view.groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.label ?? 'Sans libellé'} · {group.id.slice(0, 8)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={!choice.outcome || busy}
                    onClick={() => void classify(quote)}
                  >
                    Confirmer cette qualification
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="client-directory__actions">
            <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Précédent
            </button>
            <span>Page {page + 1}</span>
            <button type="button" disabled={!view.hasNext} onClick={() => setPage(page + 1)}>
              Suivant
            </button>
          </div>
        </>
      )}
    </section>
  );
}
